import {
  CheckLink,
  Comment,
  Context,
  Remark,
  RemarkContextTypes,
  User,
  Workflow,
} from '@app/application/communication/types'
import { orderBy } from 'lodash'

export const ALL_CATEGORY = 'all'
export const FILTERED_CATEGORY = 'filtered'
export const UNREAD_CATEGORY = 'unread'
export const PULSE_OVERVIEW_CATEGORY = 'pulse_overview'
export const COMPANY_CATEGORY = 'Company'
export const CLIENT_OVERVIEW_SECTION = 'Client overview'

export function groupCommunicationsByContext(
  values: (Remark | CheckLink)[],
  rootLedgerId: number | undefined,
  contextGetter: (
    ledgerId: number,
    contextType: string,
    contextId: number
  ) => Context,
  workflows: Workflow[]
): Record<string, Record<string, (Remark | CheckLink)[]>> {
  const result: Record<string, Record<string, (Remark | CheckLink)[]>> = {}
  for (const value of values) {
    const paths = pathsForContext(
      contextGetter(
        rootLedgerId || value.ledgerId,
        value.contextType,
        value.contextId
      ),
      workflows
    )
    for (const path of paths) {
      if (!result[path.category]) {
        result[path.category] = {}
      }
      if (!result[path.category][path.item]) {
        result[path.category][path.item] = []
      }
      result[path.category][path.item].push(value)
    }
  }
  return result
}

function pathsForContext(
  context: Context,
  workflows: Workflow[]
): { category: string; item: string }[] {
  const result = []
  if (
    [
      RemarkContextTypes.ACCOUNT,
      RemarkContextTypes.RECONCILIATION_TEXT,
    ].includes(context.type)
  ) {
    for (const workflow of workflows) {
      const inAccountContext =
        context.type === RemarkContextTypes.ACCOUNT &&
        workflow.accountIds.includes(context.id)
      const inReconciliationText =
        context.type === RemarkContextTypes.RECONCILIATION_TEXT &&
        workflow.reconciliationTextIds.includes(context.id)
      if (inAccountContext || inReconciliationText) {
        result.push({
          category: workflow.name,
          item: context.name,
        })
      }
    }
    if (result.length === 0) {
      result.push({
        category: workflows.filter((w) => w.default)[0].name,
        item: context.name,
      })
    }
  } else if (
    context.type === RemarkContextTypes.COMPANY ||
    context.type === RemarkContextTypes.COMPANY_WORKFLOW_STATUS
  ) {
    result.push({ category: COMPANY_CATEGORY, item: CLIENT_OVERVIEW_SECTION })
  } else {
    result.push({ category: context.type, item: context.name })
  }
  return result
}

export function combineNotesAndTodos(
  notes: Record<string, Record<string, (Remark | CheckLink)[]>>,
  todos: Record<string, Record<string, (Remark | CheckLink)[]>>
): Record<string, Record<string, (Remark | CheckLink)[]>> {
  const result: Record<string, Record<string, (Remark | CheckLink)[]>> = {}
  const categories = Object.keys(notes).concat(Object.keys(todos))
  for (const category of categories) {
    result[category] = {}
    const items = Object.keys(notes[category] || {}).concat(
      Object.keys(todos[category] || {})
    )
    for (const item of items) {
      const notesInItem = (notes[category] || {})[item] || []
      const todosInItem = (todos[category] || {})[item] || []
      result[category][item] = orderBy(
        notesInItem.concat(todosInItem),
        ['sortTimestamp'],
        ['desc']
      )
    }
  }
  return result
}

export function filterRemarks(
  remarks: Remark[],
  searchTerm: string,
  noteType: string,
  tagIds: number[],
  userIds: (number | 'external')[],
  commentById: (id: number) => Comment,
  userById: (id: number) => User
): Remark[] {
  const termsToSearch = []
  if (searchTerm.length > 0) {
    termsToSearch.push(searchTerm)
  }
  if (noteType === 'mentions') {
    termsToSearch.push(`@${window.Silverfin?.currentUserUsername()}`)
  }
  return filterRemarksByUsers(
    filterRemarksByTagIds(
      filterCommunicationsByStrings(remarks, commentById, termsToSearch),
      tagIds
    ),
    commentById,
    userById,
    userIds
  )
}

export function filterCommunicationsByStrings<T extends Remark | CheckLink>(
  communications: T[],
  commentById: (id: number) => Comment,
  terms: string[]
): T[] {
  if (terms.length === 0) {
    return communications
  }
  return communications.filter((communication) =>
    terms.every(
      (term) =>
        communication.text.toLowerCase().indexOf(term) !== -1 ||
        communication.commentIds.some(
          (commentId) =>
            commentById(commentId).text.toLowerCase().indexOf(term) !== -1
        )
    )
  )
}

function filterRemarksByTagIds(remarks: Remark[], tagIds: number[]): Remark[] {
  if (tagIds.length === 0) {
    return remarks
  }
  return remarks.filter((remark) =>
    remark.tagIds.some((tagId) => tagIds.includes(tagId))
  )
}

function filterRemarksByUsers(
  remarks: Remark[],
  commentById: (id: number) => Comment,
  userById: (id: number) => User,
  userIds: (number | 'external')[]
): Remark[] {
  if (userIds.length === 0) {
    return remarks
  }
  return remarks.filter(
    (remark) =>
      userIds.some((userId) =>
        userId === 'external'
          ? userById(remark.authorId).external
          : userId === remark.authorId
      ) ||
      remark.commentIds.some((commentId) => {
        const authorId = commentById(commentId).authorId
        return userIds.some((userId) =>
          userId === 'external'
            ? userById(authorId).external
            : userId === authorId
        )
      })
  )
}
